import React, { useState, useRef, useEffect } from 'react';
import { useNotifications } from '../context/NotificationContext';
import './NotificationBell.css';

interface NotificationBellProps {
  isAdmin?: boolean;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ isAdmin = false }) => {
  const { notifications, unreadCount, markAsRead, markAllAsRead, clearNotifications } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const getNotificationIcon = (type: string) => {
    switch(type) {
      case 'deposit_confirmed': return '✅';
      case 'balance_confirmed': return '🎉';
      case 'payment_confirmed': return '💰';
      case 'status_updated': return '📦';
      case 'order_created': return '🛒';
      case 'balance_payment_pending': return '⏳';
      case 'order_cancelled': return '❌';
      default: return '🔔';
    }
  };
  
  const getNotificationColor = (type: string) => {
    switch(type) {
      case 'deposit_confirmed': return '#4caf50';
      case 'balance_confirmed': return '#9c27b0';
      case 'payment_confirmed': return '#ff9800';
      case 'status_updated': return '#2196f3';
      case 'balance_payment_pending': return '#ffb300';
      case 'order_cancelled': return '#f44336';
      default: return '#ff9800';
    }
  };

  const getStatusLabel = (status?: string) => {
    switch (status) {
      case 'paid': return 'Paid';
      case 'shipped': return 'Shipped';
      case 'delivered': return 'Delivered';
      case 'returned': return 'Returned';
      case 'refunded': return 'Refunded';
      case 'cancelled': return 'Cancelled';
      default: return 'Update';
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'paid': return '#9c27b0';
      case 'shipped': return '#00bcd4';
      case 'delivered': return '#4caf50';
      case 'returned': return '#ff9800';
      case 'refunded': return '#607d8b';
      case 'cancelled': return '#f44336';
      default: return '#2196f3';
    }
  };
  
  return (
    <div className="notification-bell" ref={dropdownRef}>
      <button 
        className={`bell-button ${unreadCount > 0 ? 'has-notifications' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        🔔
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>
      
      {isOpen && (
        <div className="notification-dropdown">
          <div className="notification-header">
            <h3>Notifications</h3>
            <div className="notification-actions">
              {notifications.length > 0 && (
                <>
                  <button onClick={markAllAsRead}>Mark all read</button>
                  <button onClick={clearNotifications}>Clear all</button>
                </>
              )}
            </div>
          </div>
          
          <div className="notification-list">
            {notifications.length === 0 ? (
              <div className="no-notifications">
                <span>🔔</span>
                <p>No notifications yet</p>
              </div>
            ) : (
              notifications.map(notification => (
                <div 
                  key={notification.id} 
                  className={`notification-item ${!notification.read ? 'unread' : ''}`}
                  onClick={() => markAsRead(notification.id)}
                  style={{ borderLeftColor: getNotificationColor(notification.type) }}
                >
                  <div className="notification-icon">
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="notification-content">
                    <div className="notification-title-row">
                      <div className="notification-title">{notification.title}</div>
                      {notification.status && (
                        <span
                          className="notification-status-pill"
                          style={{ backgroundColor: getStatusColor(notification.status) }}
                        >
                          {getStatusLabel(notification.status)}
                        </span>
                      )}
                    </div>
                    <div className="notification-message">{notification.message}</div>
                    <div className="notification-time">
                      {new Date(notification.timestamp).toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;